/**
 * Real license API hooks — temporary until APP-1322 generates them.
 *
 * TODO (APP-1322): Remove this module once the license endpoints
 * (`GET`/`POST /api/v3/orgs/{orgId}/licenses`) are available in the
 * generated OpenAPI client. After merging APP-1322, run
 * `pnpm generate:http-clients` and replace imports of
 * `useListOrgLicenses` / `useCreateOrgLicense` from this module with
 * the same-named hooks from `@/generated/http-clients/v3ApiComponents`.
 *
 * Hook signatures are intentionally identical to what the generated
 * client will export so the swap is mechanical. Call sites do not need
 * to change.
 *
 * We write our own fetcher rather than using `v3ApiFetch` because
 * `v3ApiFetch` throws the raw response body, which loses the HTTP
 * status code. `CreateLicenseDialog` needs `error.status` to branch
 * between 400 / 502 (inline alert + correlationId) and other errors
 * (toast with retry). This fetcher preserves it.
 *
 * Exported types mirror the schemas in `console-backoffice-openapi-v3.yml`
 * from APP-1322, subsequently revised by APP-1396 (license-service PRs
 * #141 and #142):
 *   - LicenseOrigin is a five-value motion enum; `source` was dropped.
 *     `staff-led` was removed from the contract by hivemq-cloud PR #7086.
 *   - `reference` is the traceability field (e.g. Salesforce URL).
 *   - `clusterBindings` was added by APP-2260 (hivemq-cloud PR #7045).
 *
 * Because these types are hand-written, a field can exist in the contract
 * and in `v3ApiSchemas.ts` yet stay invisible to the app because it is
 * missing *here* — that is how `billing` was lost (APP-1493). When the
 * contract gains a field, add it to this file too, not only to the
 * generated client.
 */
import { useV3ApiContext } from '@/generated/http-clients/v3ApiContext'
import type {
  LicenseOrigin as ContractLicenseOrigin,
  LicensePackage as ContractLicensePackage,
} from '@/generated/http-clients/v3ApiSchemas'
import {
  type QueryClient,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
  useMutation,
  useQuery,
} from '@tanstack/react-query'

// --- Types mirrored from console-backoffice-openapi-v3.yml ---

export type LicenseStatus = 'ACTIVE' | 'EXPIRED' | 'REVOKED'

/**
 * Re-exported from the generated schema rather than mirrored by hand. The
 * hand-written copy is how `staff-led` outlived its removal from the contract
 * (hivemq-cloud PR #7086): the two drifted and nothing failed. Aliasing removes
 * that possibility for this type permanently.
 */
export type LicenseOrigin = ContractLicenseOrigin

/**
 * Re-exported from the generated schema rather than mirrored by hand, for
 * the same reason as LicenseOrigin above: aliasing removes the possibility
 * of this type silently drifting from the contract.
 */
export type LicensePackage = ContractLicensePackage

export type LicenseEntitlements = {
  maxConnections: number
  maxMessagesPerSecond: number
  seats: number
}

export type CreateLicenseRequestBody = {
  entitlements: LicenseEntitlements
  validityStart: number
  validityEnd: number
  name?: string
  description?: string
  /**
   * Required by the contract (`CreateLicenseRequestBody.origin` in
   * `v3ApiSchemas.ts`), and required here too: leaving it optional let a caller
   * omit a field the API demands, and the service-layer null-default would then
   * mis-tag the license as `self-service`.
   */
  origin: LicenseOrigin
  reference?: string
}

export type ClusterBindingStatus = 'BOUND' | 'UNBOUND'

/** Binding between a license and a cluster. */
export type ClusterBinding = {
  clusterId: string
  status: ClusterBindingStatus
  /** Last time the cluster was seen (UTC epoch milliseconds). */
  lastSeenAt?: number
  /**
   * The Data Intelligence agent this binding's connection string was issued
   * for. `null` / absent means "not minted yet" — it is created on this
   * cluster's next `/public/v1/license/check` by BindingProvisioningService.
   * NOT an error: the V17 backfill carried the old license-level string onto
   * exactly one binding, and the residual pre-#222/#226 cohort has none
   * either. Both self-heal.
   */
  dataIntelligenceAgentId?: string | null
}

export type LicenseResponseBody = {
  id: string
  orgId: string
  licenseKey: string
  subscriptionId?: string | null
  name?: string
  description?: string
  /**
   * The license's package tier. Stays optional: it is absent against any
   * environment still running a backoffice backend from before APP-2530.
   */
  package?: LicensePackage
  entitlements: LicenseEntitlements
  status: LicenseStatus
  validityStart: number
  validityEnd: number
  origin?: LicenseOrigin
  reference?: string
  createdAt: number
  updatedAt?: number
  /**
   * Cluster bindings for this license. The backend sends `[]` when the license
   * is bound to no cluster, but the field stays optional: it is absent against
   * any environment still running a backoffice backend from before APP-2260.
   */
  clusterBindings?: ClusterBinding[]
}

export type LicenseListResponse = {
  items: LicenseResponseBody[]
  total: number
  limit?: number
  offset?: number
}

export type ProblemDetailResponse = {
  type?: string
  detail?: string
  instance?: string
  code?: string
  correlationId?: string
}

export type ListOrgLicensesPathParams = {
  orgId: string
}

export type ListOrgLicensesQueryParams = {
  status?: LicenseStatus
  limit?: number
  offset?: number
}

export type ListOrgLicensesVariables = {
  pathParams: ListOrgLicensesPathParams
  queryParams?: ListOrgLicensesQueryParams
}

export type CreateOrgLicenseVariables = {
  pathParams: { orgId: string }
  body: CreateLicenseRequestBody
}

export type GetOrgLicenseVariables = {
  pathParams: { orgId: string; licenseId: string }
}

export type UpdateLicenseEntitlementsRequest = {
  maxConnections: number
  maxMessagesPerSecond: number
}

export type UpdateLicenseMetadataRequest = {
  name?: string
  description?: string
  /**
   * Traceability reference (e.g. a Salesforce opportunity URL). Mutable since
   * APP-2262; omit to leave unchanged.
   *
   * An **exactly empty string** clears it, and is accepted only when `origin`
   * is `self-service` (a missing origin counts as self-service). Clearing on
   * any other origin returns 422 with `code: REFERENCE_REQUIRED_FOR_ORIGIN`;
   * a whitespace-only value returns 400. Because `''` is a meaningful value
   * here and not "absent", callers must branch on `=== undefined` rather than
   * on falsiness when deciding whether the field was set.
   */
  reference?: string
}

export type RenewLicenseRequest = {
  validityEnd: number
}

export type UpdateOrgLicenseEntitlementsVariables = {
  pathParams: { orgId: string; licenseId: string }
  body: UpdateLicenseEntitlementsRequest
}

export type UpdateOrgLicenseMetadataVariables = {
  pathParams: { orgId: string; licenseId: string }
  body: UpdateLicenseMetadataRequest
}

export type RevokeOrgLicenseVariables = {
  pathParams: { orgId: string; licenseId: string }
}

export type ReactivateOrgLicenseVariables = {
  pathParams: { orgId: string; licenseId: string }
}

export type RegenerateOrgLicenseBindingConnectionStringVariables = {
  pathParams: { orgId: string; licenseId: string; clusterId: string }
}

export type ReactivateLicenseResponseBody = LicenseResponseBody & {
  reactivated: boolean
}

export type RenewOrgLicenseVariables = {
  pathParams: { orgId: string; licenseId: string }
  body: RenewLicenseRequest
}

export type LicenseApiError = {
  status?: number
  payload?: ProblemDetailResponse
  message?: string
}

// --- Fetcher ---

// Mirrors the base URL resolution in v3ApiFetcher.ts.
const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string).replace(
  /^(.+)\/api$/gm,
  '$1',
)

/**
 * The status `licenseFetch` reports when its own `timeoutMs` fires. `408` is
 * never sent by any endpoint in this file, so a caller reading it knows the
 * bound was hit at the client and that the request's outcome is unknown —
 * not that the server refused.
 */
export const CLIENT_TIMEOUT_STATUS = 408

/**
 * The problem-detail code paired with `CLIENT_TIMEOUT_STATUS`. It is minted
 * here rather than received, so it deliberately does not name any endpoint's
 * domain: it says only "we stopped waiting", and each caller decides what
 * that means for its own operation.
 */
export const CLIENT_TIMEOUT_CODE = 'CLIENT_TIMEOUT'

export type LicenseFetchOptions = {
  /**
   * Opt-in client-side bound, in milliseconds. Omitted — the default for
   * every caller in this file — the request waits on the network exactly as
   * before. Supplied, the request is aborted after `timeoutMs` and rejects
   * with `{ status: CLIENT_TIMEOUT_STATUS, payload: { code: CLIENT_TIMEOUT_CODE } }`.
   *
   * Note the abort proves nothing about the server: it may already have
   * completed the work. Callers must surface it as an *unknown* outcome, not
   * as a failure.
   */
  timeoutMs?: number
}

async function licenseRequest<TData>(
  url: string,
  init: RequestInit,
): Promise<TData> {
  const response = await window.fetch(url, init)
  if (!response.ok) {
    const payload: ProblemDetailResponse | undefined = await response
      .json()
      .catch(() => undefined)
    const error: LicenseApiError = {
      status: response.status,
      payload,
      message: response.statusText,
    }
    throw error
  }
  return response.json() as Promise<TData>
}

async function licenseFetch<TData>(
  url: string,
  init: RequestInit,
  options: LicenseFetchOptions = {},
): Promise<TData> {
  const { timeoutMs } = options
  if (timeoutMs == null) {
    // The unbounded path is left untouched: every existing caller behaves
    // exactly as it did, with no AbortSignal attached.
    return licenseRequest<TData>(url, init)
  }

  const controller = new AbortController()
  let timedOut = false
  const timer = setTimeout(() => {
    timedOut = true
    controller.abort()
  }, timeoutMs)

  try {
    return await licenseRequest<TData>(url, {
      ...init,
      signal: controller.signal,
    })
  } catch (error) {
    if (!timedOut) {
      throw error
    }
    const timeout: LicenseApiError = {
      status: CLIENT_TIMEOUT_STATUS,
      payload: { code: CLIENT_TIMEOUT_CODE },
      message: `Aborted after ${timeoutMs}ms without a response`,
    }
    throw timeout
  } finally {
    clearTimeout(timer)
  }
}

// --- Query key ---
// Follows the queryKeyFn shape from v3ApiContext.ts (path segments +
// resolved pathParams + queryParams) so that invalidateOrgLicenses —
// which uses the path-segment prefix — works identically against these
// hooks and the future generated ones.

function listOrgLicensesKey(variables: ListOrgLicensesVariables): unknown[] {
  const key: unknown[] = [
    'api',
    'v3',
    'orgs',
    variables.pathParams.orgId,
    'licenses',
  ]
  if (variables.queryParams) {
    key.push(variables.queryParams)
  }
  return key
}

// --- Hooks ---

export function useListOrgLicenses<TData = LicenseListResponse>(
  variables: ListOrgLicensesVariables,
  options?: Omit<
    UseQueryOptions<LicenseListResponse, LicenseApiError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
): UseQueryResult<TData, LicenseApiError> {
  const { fetcherOptions, queryOptions } = useV3ApiContext(options)

  return useQuery<LicenseListResponse, LicenseApiError, TData>({
    queryKey: listOrgLicensesKey(variables),
    queryFn: ({ signal }) => {
      const { orgId } = variables.pathParams
      const qp = variables.queryParams
      const params = new URLSearchParams()
      if (qp?.status) {
        params.set('status', qp.status)
      }
      if (qp?.limit != null) {
        params.set('limit', String(qp.limit))
      }
      if (qp?.offset != null) {
        params.set('offset', String(qp.offset))
      }
      const qs = params.toString()
      return licenseFetch<LicenseListResponse>(
        `${BASE_URL}/api/v3/orgs/${orgId}/licenses${qs ? `?${qs}` : ''}`,
        {
          method: 'GET',
          signal,
          headers: {
            'Content-Type': 'application/json',
            ...(fetcherOptions.headers as Record<string, string>),
          },
        },
      )
    },
    ...queryOptions,
    ...options,
  })
}

export function useCreateOrgLicense(
  options?: Omit<
    UseMutationOptions<
      LicenseResponseBody,
      LicenseApiError,
      CreateOrgLicenseVariables
    >,
    'mutationFn'
  >,
): UseMutationResult<
  LicenseResponseBody,
  LicenseApiError,
  CreateOrgLicenseVariables
> {
  const { fetcherOptions } = useV3ApiContext()

  return useMutation<
    LicenseResponseBody,
    LicenseApiError,
    CreateOrgLicenseVariables
  >({
    mutationFn: ({ pathParams: { orgId }, body }) =>
      licenseFetch<LicenseResponseBody>(
        `${BASE_URL}/api/v3/orgs/${orgId}/licenses`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(fetcherOptions.headers as Record<string, string>),
          },
          body: JSON.stringify(body),
        },
      ),
    ...options,
  })
}

export function invalidateOrgLicenses(queryClient: QueryClient, orgId: string) {
  queryClient.invalidateQueries({
    queryKey: ['api', 'v3', 'orgs', orgId, 'licenses'],
    // Invalidate the list query (GET .../licenses) but NOT a single license
    // (GET .../licenses/{licenseId}). The list prefix would otherwise match
    // the detail query too, triggering a redundant refetch right after
    // setOrgLicenseQueryData has already written the fresh data into its cache.
    // List keys end at position 5 (or carry a queryParams object there); a
    // detail key has the string licenseId at position 5.
    predicate: (query) => typeof query.queryKey[5] !== 'string',
  })
}

export function invalidateOrgLicenseDetail(
  queryClient: QueryClient,
  licenseId: string,
) {
  // Counterpart to setOrgLicenseQueryData, same predicate and same reason: the
  // detail query is keyed on the route's orgId (possibly an org slug) while
  // license.orgId is canonical, so matching on the globally-unique licenseId is
  // what makes this land. Used where the mutation response is NOT a license and
  // therefore cannot be written into the cache directly.
  //
  // Deliberately not invalidateOrgLicenses: its predicate excludes the detail
  // query, which is the only query rendering cluster bindings.
  queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[4] === 'licenses' && query.queryKey[5] === licenseId,
  })
}

export function setOrgLicenseQueryData(
  queryClient: QueryClient,
  licenseId: string,
  data: LicenseResponseBody,
) {
  // Update the cached single-license detail query for this licenseId, whatever
  // org identifier its key was built from. The detail query is keyed on the
  // route's orgId (which can be an org slug), while a mutation response carries
  // the canonical orgId in its body — the two can differ. Matching on the
  // globally-unique licenseId (a UUID) decouples this write from which orgId
  // form ended up in the key, so the detail page always re-renders. The key
  // shape is [..., 'licenses', licenseId] (see getOrgLicenseKey).
  queryClient.setQueriesData<LicenseResponseBody>(
    {
      predicate: (query) =>
        query.queryKey[4] === 'licenses' && query.queryKey[5] === licenseId,
    },
    data,
  )
}

// --- Single license hooks (APP-1408) ---
//
// Mirror the same conventions as the list / create hooks above so the
// eventual swap to the generated client is mechanical.

function getOrgLicenseKey(variables: GetOrgLicenseVariables): unknown[] {
  return [
    'api',
    'v3',
    'orgs',
    variables.pathParams.orgId,
    'licenses',
    variables.pathParams.licenseId,
  ]
}

export function useGetOrgLicense<TData = LicenseResponseBody>(
  variables: GetOrgLicenseVariables,
  options?: Omit<
    UseQueryOptions<LicenseResponseBody, LicenseApiError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
): UseQueryResult<TData, LicenseApiError> {
  const { fetcherOptions, queryOptions } = useV3ApiContext(options)

  return useQuery<LicenseResponseBody, LicenseApiError, TData>({
    queryKey: getOrgLicenseKey(variables),
    queryFn: ({ signal }) => {
      const { orgId, licenseId } = variables.pathParams
      return licenseFetch<LicenseResponseBody>(
        `${BASE_URL}/api/v3/orgs/${orgId}/licenses/${licenseId}`,
        {
          method: 'GET',
          signal,
          headers: {
            'Content-Type': 'application/json',
            ...(fetcherOptions.headers as Record<string, string>),
          },
        },
      )
    },
    ...queryOptions,
    ...options,
  })
}

export function useUpdateOrgLicenseEntitlements(
  options?: Omit<
    UseMutationOptions<
      LicenseResponseBody,
      LicenseApiError,
      UpdateOrgLicenseEntitlementsVariables
    >,
    'mutationFn'
  >,
): UseMutationResult<
  LicenseResponseBody,
  LicenseApiError,
  UpdateOrgLicenseEntitlementsVariables
> {
  const { fetcherOptions } = useV3ApiContext()

  return useMutation<
    LicenseResponseBody,
    LicenseApiError,
    UpdateOrgLicenseEntitlementsVariables
  >({
    mutationFn: ({ pathParams: { orgId, licenseId }, body }) =>
      licenseFetch<LicenseResponseBody>(
        `${BASE_URL}/api/v3/orgs/${orgId}/licenses/${licenseId}/entitlements`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(fetcherOptions.headers as Record<string, string>),
          },
          body: JSON.stringify(body),
        },
      ),
    ...options,
  })
}

export function useUpdateOrgLicenseMetadata(
  options?: Omit<
    UseMutationOptions<
      LicenseResponseBody,
      LicenseApiError,
      UpdateOrgLicenseMetadataVariables
    >,
    'mutationFn'
  >,
): UseMutationResult<
  LicenseResponseBody,
  LicenseApiError,
  UpdateOrgLicenseMetadataVariables
> {
  const { fetcherOptions } = useV3ApiContext()

  return useMutation<
    LicenseResponseBody,
    LicenseApiError,
    UpdateOrgLicenseMetadataVariables
  >({
    mutationFn: ({ pathParams: { orgId, licenseId }, body }) =>
      licenseFetch<LicenseResponseBody>(
        `${BASE_URL}/api/v3/orgs/${orgId}/licenses/${licenseId}/metadata`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(fetcherOptions.headers as Record<string, string>),
          },
          body: JSON.stringify(body),
        },
      ),
    ...options,
  })
}

export function useRevokeOrgLicense(
  options?: Omit<
    UseMutationOptions<
      LicenseResponseBody,
      LicenseApiError,
      RevokeOrgLicenseVariables
    >,
    'mutationFn'
  >,
): UseMutationResult<
  LicenseResponseBody,
  LicenseApiError,
  RevokeOrgLicenseVariables
> {
  const { fetcherOptions } = useV3ApiContext()

  return useMutation<
    LicenseResponseBody,
    LicenseApiError,
    RevokeOrgLicenseVariables
  >({
    mutationFn: ({ pathParams: { orgId, licenseId } }) =>
      licenseFetch<LicenseResponseBody>(
        `${BASE_URL}/api/v3/orgs/${orgId}/licenses/${licenseId}/revoke`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(fetcherOptions.headers as Record<string, string>),
          },
        },
      ),
    ...options,
  })
}

export function useRenewOrgLicense(
  options?: Omit<
    UseMutationOptions<
      LicenseResponseBody,
      LicenseApiError,
      RenewOrgLicenseVariables
    >,
    'mutationFn'
  >,
): UseMutationResult<
  LicenseResponseBody,
  LicenseApiError,
  RenewOrgLicenseVariables
> {
  const { fetcherOptions } = useV3ApiContext()

  return useMutation<
    LicenseResponseBody,
    LicenseApiError,
    RenewOrgLicenseVariables
  >({
    mutationFn: ({ pathParams: { orgId, licenseId }, body }) =>
      licenseFetch<LicenseResponseBody>(
        `${BASE_URL}/api/v3/orgs/${orgId}/licenses/${licenseId}/renew`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(fetcherOptions.headers as Record<string, string>),
          },
          body: JSON.stringify(body),
        },
      ),
    ...options,
  })
}

export function useReactivateOrgLicense(
  options?: Omit<
    UseMutationOptions<
      ReactivateLicenseResponseBody,
      LicenseApiError,
      ReactivateOrgLicenseVariables
    >,
    'mutationFn'
  >,
): UseMutationResult<
  ReactivateLicenseResponseBody,
  LicenseApiError,
  ReactivateOrgLicenseVariables
> {
  const { fetcherOptions } = useV3ApiContext()

  return useMutation<
    ReactivateLicenseResponseBody,
    LicenseApiError,
    ReactivateOrgLicenseVariables
  >({
    mutationFn: ({ pathParams: { orgId, licenseId } }) =>
      licenseFetch<ReactivateLicenseResponseBody>(
        `${BASE_URL}/api/v3/orgs/${orgId}/licenses/${licenseId}/reactivate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(fetcherOptions.headers as Record<string, string>),
          },
        },
      ),
    ...options,
  })
}

/**
 * How long the rotation request may run before the client stops waiting.
 *
 * The bound has to sit clear of a normal mint, or it would fire on requests
 * the server still considers healthy. The contract puts that floor at roughly
 * 30 seconds: after a `MINT_OUTCOME_UNKNOWN` the endpoint answers
 * `ROTATION_IN_PROGRESS` for about that long, so ~30s is the window in which
 * a rotation is still legitimately running. 45s clears it with margin while
 * keeping the operator's worst case under a minute — long enough that a slow
 * but succeeding mint is never cut short, short enough that a dialog which
 * refuses dismissal always releases on its own.
 */
const ROTATION_TIMEOUT_MS = 45_000

/**
 * Rotates the Data Intelligence connection string on one cluster binding
 * (APP-2548). Each binding carries its own agent and its own credential, so
 * the rotation is binding-scoped: it returns the updated `ClusterBinding` and
 * never the new string, which is readable only by that cluster's broker.
 *
 * Destructive: issuing the new string revokes the previous one, so the broker
 * on this cluster disconnects until it re-downloads the replacement.
 *
 * `admin` only. Every other license endpoint here also admits `gtm`.
 *
 * Alone among the mutations here it is bounded by `ROTATION_TIMEOUT_MS`,
 * because the dialog that drives it refuses every dismissal while the request
 * is pending: an unbounded request would leave `isPending` true forever and
 * trap the operator in a modal only a page reload escapes.
 */
export function useRegenerateOrgLicenseBindingConnectionString(
  options?: Omit<
    UseMutationOptions<
      ClusterBinding,
      LicenseApiError,
      RegenerateOrgLicenseBindingConnectionStringVariables
    >,
    'mutationFn'
  >,
): UseMutationResult<
  ClusterBinding,
  LicenseApiError,
  RegenerateOrgLicenseBindingConnectionStringVariables
> {
  const { fetcherOptions } = useV3ApiContext()

  return useMutation<
    ClusterBinding,
    LicenseApiError,
    RegenerateOrgLicenseBindingConnectionStringVariables
  >({
    mutationFn: ({ pathParams: { orgId, licenseId, clusterId } }) =>
      licenseFetch<ClusterBinding>(
        /* `clusterId` is opaque to the console — the contract relays it
           unparsed — so a `/`, `?` or `#` in it would otherwise rewrite the
           path of a destructive POST and target a different resource. The
           other two segments are encoded for the same reason, not because
           they are known to be looser. */
        `${BASE_URL}/api/v3/orgs/${encodeURIComponent(orgId)}/licenses/${encodeURIComponent(licenseId)}/bindings/${encodeURIComponent(clusterId)}/connection-string`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(fetcherOptions.headers as Record<string, string>),
          },
        },
        { timeoutMs: ROTATION_TIMEOUT_MS },
      ),
    ...options,
  })
}
